<template>
  <ModalComponent
    ref="modalRef"
    size="lg"
    submitButtonText="Close"
    :hideSubmitButton="true"
    @close="$emit('close')"
  >
    <template #title>
      <span class="cg-micro d-block"
        >{{ domainName }} · {{ detail.scope }}</span
      >
      {{ detail.period }}
    </template>
    <template #body>
      <p class="cg-micro mb-2">Consumption by consumption location</p>
      <table class="table table-sm mb-4 align-middle small">
        <thead>
          <tr>
            <th class="cg-micro">Consumption location</th>
            <th class="cg-micro">Basis</th>
            <th class="cg-micro text-end">MWh</th>
          </tr>
        </thead>
        <tbody class="cg-tabular">
          <tr v-for="mp in detail.meters" :key="mp.id">
            <td>
              <span class="fw-medium">{{ mp.name }}</span
              ><span class="d-block text-body-secondary">{{ mp.entity }}</span>
            </td>
            <td><ToneBadge :tone="mp.tone" :label="mp.basis" /></td>
            <td class="text-end fw-semibold">{{ fmt(mp.volume) }}</td>
          </tr>
          <tr class="fw-semibold border-top">
            <td colspan="2">Total</td>
            <td class="text-end">{{ fmt(detail.total) }}</td>
          </tr>
        </tbody>
      </table>

      <template v-if="isMonthly && detail.month">
        <p class="cg-micro mb-2">Earmarked GOs · this portfolio only</p>
        <table class="table table-sm mb-3 cg-key-table">
          <tbody class="cg-tabular">
            <tr>
              <td class="text-body-secondary">Cancelled to this month</td>
              <td>
                {{ detail.month.locked ? fmt(detail.month.locked) : "—" }}
              </td>
            </tr>
            <tr>
              <td class="text-body-secondary">In account · still movable</td>
              <td>
                {{ detail.month.flexible ? fmt(detail.month.flexible) : "—" }}
              </td>
            </tr>
            <tr>
              <td class="text-body-secondary">Expected from the EECS pool</td>
              <td>
                {{ detail.month.expected ? fmt(detail.month.expected) : "—" }}
              </td>
            </tr>
            <tr class="fw-semibold">
              <td>Gap</td>
              <td :class="{ 'text-warning-emphasis': detail.month.gap > 0 }">
                {{ detail.month.gap ? fmt(detail.month.gap) : "0" }}
              </td>
            </tr>
          </tbody>
        </table>
        <p class="text-body-secondary small">
          {{ detail.month.event }}. Production month must equal consumption
          month, so only {{ detail.period }} production can serve this
          portfolio.
        </p>
        <button
          v-if="detail.month.gap > 0"
          type="button"
          class="btn btn-sm btn-primary"
          @click="$emit('resolve')"
        >
          Review proposed resolution
        </button>
      </template>
      <p v-else class="mb-0 text-body-secondary small">
        This domain matches annually, so this month is a consumption record
        rather than its own portfolio. Any 2026 production month can serve it.
      </p>
    </template>
  </ModalComponent>
</template>

<script setup lang="ts">
import type { MonthDetail } from "@/compliance_groups/fixture/derive";
import { fmt } from "@/compliance_groups/fixture/format";
import ModalComponent from "@/components/common/ModalComponent.vue";
import { onMounted, useTemplateRef } from "vue";
import ToneBadge from "./ToneBadge.vue";

defineProps<{ detail: MonthDetail; domainName: string; isMonthly: boolean }>();
defineEmits<{ close: []; resolve: [] }>();

const modalRef =
  useTemplateRef<InstanceType<typeof ModalComponent>>("modalRef");
onMounted(() => modalRef.value?.toggleModal());
</script>
